/**
 * CalendarView — piece publication calendar (Hono-backed).
 *
 * S-080 / FEAT-006 (Phase 8 fix). The legacy `/calendar` route was the
 * publication calendar (pieces grouped by `publication_date`); Phase 8
 * mistakenly rewrote it as a social-amplification grid. That social
 * grid is now at `/social` (SocialCalendarView). This file is the
 * new `/calendar` — pieces grouped by their target publication date,
 * read from GET /pieces.
 *
 * What it does:
 *   - No workspace selector of its own — FEAT-087 moved the picker
 *     so cross-workspace navigation behaves the same as
 *     PublicationsView / TeamView / SocialCalendarView.
 *   - Month grid (default) or Day view. Anchor + prev/next/today +
 *     "Day" toggle mirror SocialCalendarView's chrome.
 *   - GET /pieces?agencyclient_id=N — the list endpoint returns rows
 *     filtered by tenant + agencyclient scope (RLS). The SPA filters
 *     the result client-side by month window + status. List excludes
 *     soft-deleted pieces by default per route.
 *   - Status filter chips bound to the locked Kanban column labels
 *     (Ideas / In Progress / Awaiting Approval / Scheduled / Published)
 *     per the feedback_kanban_columns memory. Mapping uses the same
 *     helper as KanbanView — pieces.ui_state -> KanbanColumn.
 *   - Per-card display:
 *       Title (truncated to ~40 chars)
 *       Publication name (resolved via /publications/:id lookup)
 *       Status pill in the column tone
 *       Publication date HH:MM (publication_date is timestamptz)
 *   - Backlog strip BELOW the grid (S-176): pieces with no publication_date
 *     set. Sorted to bottom so undated pieces don't dominate the view.
 *   - Click → navigate to /piece/:id (PieceView).
 *
 * S-175 / FEAT-020 — month/year header label + jump-to-date pickers.
 *   Month view: "June 2026" heading + <select> month/<select> year (±2).
 *   Day view: <input type="date"> to jump to any day directly.
 *
 * S-176 / FEAT-020 — undated pieces (publicationDate === null) sorted
 *   to bottom. Client-side: BacklogStrip rendered after the grid, not
 *   before. The backlog bucket already separates nulls, so the fix is
 *   purely render-order.
 *
 * Read-only navigation: this view does NOT mutate pieces. Editing,
 * scheduling, and publishing happen on the piece view + kanban.
 *
 * Status semantics (per project_publication_date_gate memory):
 *   - publication_date is the TARGET (gate). The piece may not have
 *     published yet even if the date is in the past — that's a
 *     ui_state=ai_publishing or status=approved waiting on the gate.
 *   - The actual published date lives on publish_results.published_at
 *     (FEAT-004) but is not surfaced here at v1 — the column pill
 *     conveys "is this thing actually live yet" via ui_state.
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApiQuery } from "@/lib/api";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { useWorkspaceContext } from "@/lib/workspace-context";
import { useIsMobile } from "@/lib/useIsMobile";
import {
  KANBAN_COLUMNS,
  kanbanColumnForPiece,
  type KanbanColumn,
} from "@/lib/ui-state";
import { CreatePieceModal } from "@/components/CreatePieceModal";
import { workspaceBorderStyle } from "@/lib/workspace-color";
import {
  type CalendarMode,
  CALENDAR_MODES,
  CALENDAR_MODE_LABEL,
  DEFAULT_CALENDAR_MODE,
  parseCalendarMode,
  localDateIso,
  rangeDays,
  fourWeekRows,
  stepAnchor,
} from "@/lib/calendar-range";

// ---------- API row shapes ----------

interface PieceListRow {
  id: number;
  tenant_id: number;
  agencyclient_id: number;
  publication_id: number;
  slug: string;
  title: string;
  abstract: string | null;
  content_type: string;
  content_format: string;
  content_origin: string;
  language: string;
  tone: string;
  target_word_count: number | null;
  status: string;
  ui_state: string;
  score: number | null;
  focus_keyword: string | null;
  publication_date: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface PiecesListResponse {
  data: PieceListRow[];
  meta: { total: number; limit: number; offset: number };
}

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface Publication {
  id: number;
  name: string;
  slug: string;
}

// ---------- Status semantics ----------
//
// We bind the publication calendar's filter chips to the Kanban columns
// per the locked-label memory. `kanbanColumnForPiece` reads ui_state
// (+ status/generation_phases for the proposal-with-phases edge) and
// returns one of the 5 column ids.

const COLUMN_LABEL: Record<KanbanColumn, string> = Object.fromEntries(
  KANBAN_COLUMNS.map((c) => [c.id, c.title])
) as Record<KanbanColumn, string>;

const COLUMN_PILL_CLASS: Record<KanbanColumn, string> = {
  ideas: "pill-muted",
  ai_working: "pill-warn",
  awaiting_human: "pill-info",
  scheduled: "pill-ok",
  published: "pill-ok",
};

const ALL_COLUMNS: readonly KanbanColumn[] = KANBAN_COLUMNS.map((c) => c.id);

// FEAT-035 — mobile month cells render one small square per scheduled piece,
// coloured by the piece's pipeline stage (mirrors the kanban column tone).
const COLUMN_SQUARE_TONE: Record<KanbanColumn, string> = {
  ideas: "neutral",
  ai_working: "yellow",
  awaiting_human: "blue",
  scheduled: "green",
  published: "neutral",
};

function squareTone(p: PieceListRow): string {
  const col = pieceColumn(p);
  return col ? COLUMN_SQUARE_TONE[col] : "neutral";
}

/** Adapt a Hono camelCase piece row to the snake_case shape `kanbanColumnForPiece` expects. */
function pieceColumn(p: PieceListRow): KanbanColumn | null {
  return kanbanColumnForPiece({
    ui_state: p.ui_state,
    status: p.status,
    // List response omits generation_phases; the proposal-with-phases
    // edge falls through to kanbanColumnFor(ui_state), which is fine
    // for v1 — Scout-pending rows still surface in Awaiting Approval
    // because the orchestrator sets ui_state to ai_outlining and the
    // /pieces filter retains them. Operators can dive into the piece
    // for the full state.
  });
}

// ---------- Date helpers ----------

function pad(n: number): string {
  return String(n).padStart(2, "0");
}


function monthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function fmtHm(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "—";
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// S-175 — month name list for the jump-to-date select.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

// ---------- Top-level view ----------

// BUG-068 — workspace selection now lives in the shared WorkspaceProvider
// (mounted once in Shell.tsx) instead of this view's own sessionStorage-
// backed state, so switching workspace here carries over to
// Kanban/Social and vice versa.
export function CalendarView() {
  const {
    workspaces,
    isLoading: wsLoading,
    error: wsError,
    selectedId,
  } = useWorkspaceContext();

  if (wsLoading) {
    return (
      <main className="main">
        <div className="state-loading">Loading calendar…</div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main">
        <div className="state-error">
          {wsError instanceof ApiError && wsError.status === 403
            ? "You don't have permission to view the calendar."
            : (wsError.message ?? "Could not load workspaces.")}
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main">
        <div className="cal-wrap">
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings to start scheduling piece
              publications.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <CalendarBody
      workspaces={workspaces}
      selectedId={selectedId}
    />
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedId: number | null;
}

/** FEAT-087 — the calendar reopens on the range last used, defaulting to
 * Month. The parsing (and the fallback for an unrecognised stored value)
 * lives in lib/calendar-range with the rest of the range logic. */
const CALENDAR_MODE_KEY = "ccm:calendar_mode";

function readStoredMode(): CalendarMode {
  if (typeof window === "undefined") return DEFAULT_CALENDAR_MODE;
  try {
    return parseCalendarMode(window.localStorage.getItem(CALENDAR_MODE_KEY));
  } catch {
    // localStorage unavailable (private mode, blocked site data).
    return DEFAULT_CALENDAR_MODE;
  }
}

function writeStoredMode(mode: CalendarMode): void {
  try {
    window.localStorage.setItem(CALENDAR_MODE_KEY, mode);
  } catch {
    // ignore — the view still works, it just will not be remembered.
  }
}

function CalendarBody({ workspaces, selectedId }: BodyProps) {
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? workspaces[0],
    [workspaces, selectedId]
  );
  const activeId = activeWs.id;
  const allWorkspaces = selectedId === 0;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and create-piece mutations target it.
  // BUG-068 — skip this when "All workspaces" is selected: activeId falls
  // back to workspaces[0] in that mode (it isn't a real single-workspace
  // selection), so syncing it would silently repoint the real backend
  // active workspace to workspaces[0] every time All Workspaces is chosen.
  useEnsureActiveWorkspace(allWorkspaces ? undefined : activeId);

  const navigate = useNavigate();
  const qc = useQueryClient();
  // FEAT-087 (founder addition, not in the design): the calendar reopens on
  // whichever range you last chose, defaulting to Month. Persisted in
  // localStorage rather than sessionStorage so it survives new tabs and
  // restarts — "last selected" should mean last, not last in this tab —
  // and rather than in Clerk metadata, which would put a network write on
  // every view switch for what is UI convenience, not an account setting.
  const [mode, setMode] = useState<CalendarMode>(readStoredMode);
  const [anchor, setAnchor] = useState<Date>(() => new Date());

  // Only an explicit choice is remembered. The mobile auto-switch below
  // deliberately does not write, or a phone visit would silently change
  // what the desktop opens on.
  const chooseMode = useCallback((next: CalendarMode) => {
    setMode(next);
    writeStoredMode(next);
  }, []);

  // FEAT-035 — on mobile the primary lens is a single day. Drop into Day
  // view when we cross into the mobile breakpoint.
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isMobile) setMode("day");
  }, [isMobile]);
  const [columnFilters, setColumnFilters] = useState<Set<KanbanColumn>>(
    () => new Set(ALL_COLUMNS)
  );
  // S-085 — + New piece modal state. Mirrors KanbanView, sharing the
  // CreatePieceModal component so both surfaces use the same fields,
  // the same POST /pieces call, and the same publication-dropdown
  // fetch.
  const [showCreate, setShowCreate] = useState(false);



  // GET /pieces — workspace-scoped. Disabled when allWorkspaces is on.
  const { data: wsData, isLoading: wsLoading, error: wsError } =
    useApiQuery<PiecesListResponse>(
      ["pieces", activeId],
      `/pieces?agencyclient_id=${activeId}`,
      { refetchOnWindowFocus: true, enabled: !allWorkspaces }
    );

  // GET /overview/pieces — spans all workspaces the user is a member of.
  // Enabled only when allWorkspaces toggle is on.
  const { data: overviewData, isLoading: overviewLoading, error: overviewError } =
    useApiQuery<PiecesListResponse>(
      ["overview-pieces", "calendar-all"],
      `/overview/pieces?limit=200`,
      { refetchOnWindowFocus: true, enabled: allWorkspaces }
    );

  const data = allWorkspaces ? overviewData : wsData;
  const isLoading = allWorkspaces ? overviewLoading : wsLoading;
  const error = allWorkspaces ? overviewError : wsError;

  const allPieces = data?.data ?? [];

  // When allWorkspaces is on, apply workspace color borders to cards from
  // workspaces other than the currently active one.
  const getCardStyle = allWorkspaces
    ? (row: PieceListRow): CSSProperties | undefined =>
        row.agencyclient_id !== activeId
          ? workspaceBorderStyle(row.agencyclient_id)
          : undefined
    : undefined;

  // Filter by column membership (status chip).
  const filteredPieces = useMemo(() => {
    return allPieces.filter((p) => {
      const col = pieceColumn(p);
      if (!col) return false;
      return columnFilters.has(col);
    });
  }, [allPieces, columnFilters]);

  // Group by local-tz YYYY-MM-DD on publication_date. Pieces with
  // no publication_date land in a "backlog" bucket so unscheduled
  // drafts stay visible above the grid.
  const grouped = useMemo(() => {
    const byDate = new Map<string, PieceListRow[]>();
    const backlog: PieceListRow[] = [];
    for (const p of filteredPieces) {
      if (!p.publication_date) {
        backlog.push(p);
        continue;
      }
      const iso = localDateIso(new Date(p.publication_date));
      const list = byDate.get(iso) ?? [];
      list.push(p);
      byDate.set(iso, list);
    }
    for (const list of byDate.values()) {
      list.sort((a, b) => {
        const ta = a.publication_date ? new Date(a.publication_date).getTime() : 0;
        const tb = b.publication_date ? new Date(b.publication_date).getTime() : 0;
        return ta - tb;
      });
    }
    return { byDate, backlog };
  }, [filteredPieces]);

  function toggleColumn(col: KanbanColumn) {
    setColumnFilters((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  }

  function toggleAll() {
    setColumnFilters((prev) =>
      prev.size === ALL_COLUMNS.length ? new Set() : new Set(ALL_COLUMNS)
    );
  }

  const today = new Date();
  const todayIso = localDateIso(today);

  function handleOpenPiece(id: number) {
    navigate(`/piece/${id}`);
  }

  // FEAT-035 — mobile month cells select a whole day (no per-piece tap),
  // then switch to Day view to read that day's schedule.
  function handleSelectDay(date: Date) {
    setAnchor(date);
    setMode("day");
  }

  // S-371 — an adjacent-month cell moves the month rather than dropping into
  // a day view: the reader clicked it to see where that work sits, not to
  // leave the month behind.
  function handleJumpMonth(date: Date) {
    setAnchor(date);
  }

  return (
    <main className="main">
      <div className="cal-wrap">
        <div className="cal-head">
          <div>
            <h1 className="view-title">Publication Calendar</h1>
            <p className="view-sub">
              {rangeSummary(
                mode,
                anchor,
                grouped.byDate,
                allPieces.length,
                filteredPieces.length
              )}
            </p>
          </div>
          <div className="cal-head-r">
            <div className="cal-modes" role="tablist" aria-label="Calendar range">
              {/* FEAT-087 — the range selector lives inside the calendar
                  rather than in a bar shared with Queue and Board: those are
                  separate screens, while these four really are views of one
                  thing. */}
              {CALENDAR_MODES.map((m) => (
                <button
                  key={m}
                  role="tab"
                  aria-selected={mode === m}
                  className={`cal-mode ${mode === m ? "is-active" : ""}`}
                  onClick={() => chooseMode(m)}
                >
                  {CALENDAR_MODE_LABEL[m]}
                </button>
              ))}
            </div>
            <div className="cal-nav">
              <button
                className="cal-nav-btn"
                onClick={() => setAnchor(stepAnchor(anchor, mode, -1))}
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                className="cal-nav-today"
                onClick={() => setAnchor(new Date())}
              >
                Today
              </button>
              <button
                className="cal-nav-btn"
                onClick={() => setAnchor(stepAnchor(anchor, mode, 1))}
                aria-label="Next"
              >
                ›
              </button>
            </div>
            {/* S-085 — + New piece entry point on the calendar header.
                Mirrors the Kanban + New affordance, using the shared
                CreatePieceModal so both surfaces stay in sync. */}
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowCreate(true)}
              style={{ marginLeft: 12 }}
            >
              + New piece
            </button>
          </div>
        </div>

        <div className="cal-status-chips">
          <button
            type="button"
            className={`cal-chip ${
              columnFilters.size === ALL_COLUMNS.length ? "is-active" : ""
            }`}
            onClick={toggleAll}
          >
            All
          </button>
          {ALL_COLUMNS.map((c) => (
            <button
              key={c}
              type="button"
              className={`cal-chip ${columnFilters.has(c) ? "is-active" : ""}`}
              onClick={() => toggleColumn(c)}
            >
              <span className={`pill ${COLUMN_PILL_CLASS[c] ?? "pill-muted"}`}>
                {COLUMN_LABEL[c] ?? c}
              </span>
            </button>
          ))}
        </div>

        {isLoading && <div className="state-loading">Loading pieces…</div>}

        {error && !isLoading && (
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view this workspace's calendar."
              : (error.message ?? "Could not load pieces.")}
          </div>
        )}

        {/* S-175 — month/year label + jump-to-date pickers */}
        {!isLoading && !error && mode === "month" && (
          <div className="cal-month-nav" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <strong className="cal-month-label" style={{ fontSize: 18, minWidth: 140 }}>
              {anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
            </strong>
            <select
              className="settings-select"
              style={{ width: "auto" }}
              value={anchor.getMonth()}
              onChange={(e) => {
                const next = new Date(anchor);
                next.setMonth(Number(e.target.value));
                setAnchor(next);
              }}
              aria-label="Jump to month"
            >
              {MONTH_NAMES.map((name, idx) => (
                <option key={name} value={idx}>{name}</option>
              ))}
            </select>
            <select
              className="settings-select"
              style={{ width: "auto" }}
              value={anchor.getFullYear()}
              onChange={(e) => {
                const next = new Date(anchor);
                next.setFullYear(Number(e.target.value));
                setAnchor(next);
              }}
              aria-label="Jump to year"
            >
              {Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}

        {!isLoading && !error && mode === "day" && (
          <div className="cal-day-jump" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <label className="settings-label" style={{ marginBottom: 0 }}>Jump to date</label>
            <input
              className="settings-input"
              type="date"
              style={{ width: "auto" }}
              value={localDateIso(anchor)}
              onChange={(e) => {
                if (e.target.value) setAnchor(new Date(e.target.value + "T00:00:00"));
              }}
              aria-label="Jump to date"
            />
          </div>
        )}

        {!isLoading && !error && allPieces.length === 0 && (
          <div className="settings-empty" style={{ marginTop: 16 }}>
            <h2>No pieces scheduled</h2>
            <p>
              Create pieces in the Kanban board and set their publication
              dates to populate this calendar.
            </p>
          </div>
        )}

        {/* S-176 — month grid and day view render BEFORE the backlog strip so
            undated pieces (no publication_date) appear at the bottom, not the top. */}
        {!isLoading && !error && mode === "month" && allPieces.length > 0 && (
          <MonthGrid
            anchor={anchor}
            byDate={grouped.byDate}
            todayIso={todayIso}
            onOpen={handleOpenPiece}
            isMobile={isMobile}
            onSelectDay={handleSelectDay}
            onJumpMonth={handleJumpMonth}
            getCardStyle={getCardStyle}
          />
        )}

        {!isLoading &&
          !error &&
          (mode === "week" || mode === "fourweeks") &&
          allPieces.length > 0 && (
            <WeekRanges
              anchor={anchor}
              mode={mode}
              byDate={grouped.byDate}
              todayIso={todayIso}
              onOpen={handleOpenPiece}
              onSelectDay={handleSelectDay}
              getCardStyle={getCardStyle}
            />
          )}

        {!isLoading && !error && mode === "day" && allPieces.length > 0 && (
          <DayView
            anchor={anchor}
            rows={grouped.byDate.get(localDateIso(anchor)) ?? []}
            isToday={localDateIso(anchor) === todayIso}
            onOpen={handleOpenPiece}
            getCardStyle={getCardStyle}
          />
        )}

        {/* S-176 — backlog strip renders after the grid (bottom position). */}
        {!isLoading && !error && allPieces.length > 0 && grouped.backlog.length > 0 && (
          <BacklogStrip rows={grouped.backlog} onOpen={handleOpenPiece} getCardStyle={getCardStyle} />
        )}
      </div>

      {showCreate && (
        <CreatePieceModal
          agencyclientId={activeId}
          onClose={() => setShowCreate(false)}
          onCreated={(pieceId) => {
            void qc.invalidateQueries({ queryKey: ["pieces", activeId] });
            void qc.invalidateQueries({ queryKey: ["overview-pieces", "calendar-all"] });
            setShowCreate(false);
            handleOpenPiece(pieceId);
          }}
        />
      )}
    </main>
  );
}


/**
 * The one-line summary under the title. Counts come from the same byDate map
 * the grid renders, so the header can never disagree with the cells below it
 * — the failure the handoff warns about when ranges keep their own arrays.
 */
function rangeSummary(
  mode: CalendarMode,
  anchor: Date,
  byDate: Map<string, PieceListRow[]>,
  total: number,
  visible: number
): string {
  const days = rangeDays(anchor, mode);
  const inRange = days.reduce((n, iso) => n + (byDate.get(iso)?.length ?? 0), 0);

  if (mode === "day") {
    const label = anchor.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    return `${label} · ${inRange} ${inRange === 1 ? "piece" : "pieces"}`;
  }
  if (mode === "month") {
    return `${monthLabel(anchor)} · ${total} pieces (${visible} visible)`;
  }
  const first = new Date(`${days[0]}T00:00:00`);
  const last = new Date(`${days[days.length - 1]}T00:00:00`);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(first)} – ${fmt(last)} · ${inRange} ${
    inRange === 1 ? "piece" : "pieces"
  }`;
}

// ---------- Week and 4-weeks ----------

interface WeekRangesProps {
  anchor: Date;
  mode: "week" | "fourweeks";
  byDate: Map<string, PieceListRow[]>;
  todayIso: string;
  onOpen: (id: number) => void;
  onSelectDay: (date: Date) => void;
  getCardStyle?: (row: PieceListRow) => CSSProperties | undefined;
}

/**
 * Week is one 7-column row; 4 weeks is four of them, this week first, each
 * labelled with its own count. Both read the same byDate map the month grid
 * does — they differ only in which days they ask it for.
 *
 * 4 weeks deliberately has no month edge: it runs 28 days from this week's
 * Sunday, so work three weeks out is visible without paging.
 */
function WeekRanges({
  anchor,
  mode,
  byDate,
  todayIso,
  onOpen,
  onSelectDay,
  getCardStyle,
}: WeekRangesProps) {
  const rows = mode === "week" ? [rangeDays(anchor, "week")] : fourWeekRows(anchor);

  return (
    <div className="cal-weeks">
      {rows.map((days, i) => {
        const count = days.reduce(
          (n, iso) => n + (byDate.get(iso)?.length ?? 0),
          0
        );
        const first = new Date(`${days[0]}T00:00:00`);
        const last = new Date(`${days[6]}T00:00:00`);
        const fmt = (d: Date) =>
          d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        return (
          <section className="cal-week" key={days[0]}>
            {mode === "fourweeks" && (
              <header className="cal-week-head">
                <span className="cal-week-label">
                  {i === 0 ? "This week" : `${fmt(first)} – ${fmt(last)}`}
                </span>
                <span className="cal-week-rule" />
                <span className="cal-week-count">
                  {count} {count === 1 ? "piece" : "pieces"}
                </span>
              </header>
            )}
            <div className="cal-grid cal-grid-week">
              {DOW.map((d) => (
                <div key={d} className="cal-dow">
                  {d}
                </div>
              ))}
              {days.map((iso) => {
                const date = new Date(`${iso}T00:00:00`);
                const items = byDate.get(iso) ?? [];
                const isToday = iso === todayIso;
                return (
                  <div
                    key={iso}
                    className={`cal-cell ${isToday ? "is-today" : ""}`}
                  >
                    <button
                      className="cal-day cal-day-btn"
                      onClick={() => onSelectDay(date)}
                      title="Open this day"
                    >
                      {date.getDate()}
                      {isToday && <span className="cal-today">today</span>}
                    </button>
                    <div className="cal-items">
                      {items.map((row) => (
                        <PieceCard
                          key={row.id}
                          row={row}
                          onOpen={onOpen}
                          extraStyle={getCardStyle?.(row)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---------- Month grid ----------

interface MonthGridProps {
  anchor: Date;
  byDate: Map<string, PieceListRow[]>;
  todayIso: string;
  onOpen: (id: number) => void;
  isMobile: boolean;
  onSelectDay: (date: Date) => void;
  /** An adjacent-month day moves the month, rather than opening a day. */
  onJumpMonth: (date: Date) => void;
  getCardStyle?: (row: PieceListRow) => CSSProperties | undefined;
}

function MonthGrid({
  anchor,
  byDate,
  todayIso,
  onOpen,
  isMobile,
  onSelectDay,
  onJumpMonth,
  getCardStyle,
}: MonthGridProps) {
  // S-371 — the month runs whole weeks and the leading/trailing days are
  // REAL cells, not blanks. A blank cell claims "nothing here" about a day
  // that may well have work on it; showing it muted, with its pieces, tells
  // the truth and lets the reader click through to that month.
  const cells = useMemo(() => {
    const month = anchor.getMonth();
    return rangeDays(anchor, "month").map((iso) => {
      const date = new Date(`${iso}T00:00:00`);
      return { key: iso, iso, date, outside: date.getMonth() !== month };
    });
  }, [anchor]);

  return (
    <div className={`cal-grid cal-grid-month${isMobile ? " cal-grid-month-m" : ""}`}>
      {DOW.map((d) => (
        <div key={d} className="cal-dow">
          {isMobile ? d[0] : d}
        </div>
      ))}
      {cells.map((c) => {
        const isToday = c.iso === todayIso;
        const items = byDate.get(c.iso) ?? [];

        // FEAT-035 — mobile: a square count-summary cell. The whole cell is
        // the tap target (no per-piece selection); it opens the day.
        if (isMobile) {
          return (
            <button
              key={c.key}
              className={`cal-cell cal-cell-m${isToday ? " is-today" : ""}${
                items.length ? " has-items" : ""
              }${c.outside ? " is-outside" : ""}`}
              onClick={() => (c.outside ? onJumpMonth(c.date) : onSelectDay(c.date))}
            >
              <span className="cal-day-m">{c.date.getDate()}</span>
              <span className="cal-cell-squares">
                {items.slice(0, 6).map((row) => (
                  <span key={row.id} className={`cal-sq t-${squareTone(row)}`} />
                ))}
                {items.length > 6 && (
                  <span className="cal-sq-more">+{items.length - 6}</span>
                )}
              </span>
            </button>
          );
        }

        return (
          <div
            key={c.key}
            className={`cal-cell ${isToday ? "is-today" : ""}${
              c.outside ? " is-outside" : ""
            }`}
          >
            <button
              className="cal-day cal-day-btn"
              onClick={() => (c.outside ? onJumpMonth(c.date) : onSelectDay(c.date))}
              title={c.outside ? "Jump to this month" : "Open this day"}
            >
              {c.date.getDate()}
              {isToday && <span className="cal-today">today</span>}
            </button>
            <div className="cal-items">
              {items.slice(0, 3).map((row) => (
                <PieceCard key={row.id} row={row} onOpen={onOpen} extraStyle={getCardStyle?.(row)} />
              ))}
              {items.length > 3 && (
                <span className="cal-overflow">+{items.length - 3} more</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Day view ----------

interface DayViewProps {
  anchor: Date;
  rows: PieceListRow[];
  isToday: boolean;
  onOpen: (id: number) => void;
  getCardStyle?: (row: PieceListRow) => CSSProperties | undefined;
}

function DayView({ anchor, rows, isToday, onOpen, getCardStyle }: DayViewProps) {
  return (
    <div className="cal-day-wrap">
      <div className="cal-day-head">
        <span className="cal-day-num">{anchor.getDate()}</span>
        <div className="cal-day-meta">
          <span className="cal-day-dow">{DOW[anchor.getDay()]}</span>
          <span className="cal-day-date">
            {anchor.toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>
        {isToday && <span className="cal-today cal-today-lg">today</span>}
      </div>
      <div className="cal-day-body">
        {rows.length === 0 && (
          <div className="cal-day-empty">
            Nothing scheduled for this day.
          </div>
        )}
        {rows.map((row) => (
          <PieceCard key={row.id} row={row} onOpen={onOpen} large extraStyle={getCardStyle?.(row)} />
        ))}
      </div>
    </div>
  );
}

// ---------- Backlog strip (pieces without publication_date) ----------

function BacklogStrip({
  rows,
  onOpen,
  getCardStyle,
}: {
  rows: PieceListRow[];
  onOpen: (id: number) => void;
  getCardStyle?: (row: PieceListRow) => CSSProperties | undefined;
}) {
  return (
    <div className="cal-backlog">
      <div className="cal-backlog-head">
        <strong>No date set</strong>
        <span className="cal-backlog-hint">
          {rows.length} piece{rows.length === 1 ? "" : "s"} without a
          target publication date. Open a card to set one.
        </span>
      </div>
      <div className="cal-backlog-list">
        {rows.map((row) => (
          <PieceCard key={row.id} row={row} onOpen={onOpen} large extraStyle={getCardStyle?.(row)} />
        ))}
      </div>
    </div>
  );
}

// ---------- Card ----------

interface PieceCardProps {
  row: PieceListRow;
  onOpen: (id: number) => void;
  large?: boolean;
  extraStyle?: CSSProperties;
}

function PieceCard({ row, onOpen, large, extraStyle }: PieceCardProps) {
  const pub = usePublicationLookup(row.publication_id);
  const col = pieceColumn(row);
  const pillCls = col ? COLUMN_PILL_CLASS[col] : "pill-muted";
  const colLabel = col ? COLUMN_LABEL[col] : row.status;
  const time = fmtHm(row.publication_date);
  const title = truncate(row.title, 40);
  const pubLabel = pub?.name ?? `publication #${row.publication_id}`;

  return (
    <button
      type="button"
      className={`cal-card ${large ? "cal-card-lg" : ""}`}
      onClick={() => onOpen(row.id)}
      title={`${row.title} · ${pubLabel} · ${time}`}
      style={extraStyle}
    >
      <span className="cal-card-row cal-card-row-top">
        <span className="cal-card-platform">
          <span className="cal-card-time mono">{time}</span>
        </span>
        <span className={`pill ${pillCls}`}>{colLabel}</span>
      </span>
      <span className="cal-card-amp">{title}</span>
      <span className="cal-card-client">
        <span className="cal-card-client-label">Publication</span>
        <span className="cal-card-client-name">{pubLabel}</span>
      </span>
    </button>
  );
}

// ---------- Lookup hooks (cached via TanStack Query) ----------

function usePublicationLookup(id: number) {
  const { data } = useApiQuery<Publication>(
    ["publication", id],
    `/publications/${id}`,
    {
      staleTime: 5 * 60_000,
      retry: false,
    }
  );
  return data;
}
